import {EBreakpoint, RESIZE_EVENTS} from '@/common/scripts/constants';
import type {IMenuContent, IMenuOptions} from './types';
import {initSlider} from '@/common/scripts/common';
import itemTemplate from './templates/menu-item.twig';
import Observer from '@/common/scripts/observer';
import {Utils} from '@/common/scripts/utils';

const CLASS_ACTIVE = 'is-active';
const CLASS_OPEN = 'is-open';
const CLASS_HIDE = 'is-hide';

const observer = new Observer();

export class Menu {
    private wrapper: HTMLElement;
    private headerWrap: HTMLElement;
    private openButton: HTMLElement;
    private overlay: HTMLElement | null;
    private isMobile: boolean;
    private isFullHD: boolean;
    private mobileMenu: HTMLElement | null;
    private mobileMenuDynamic: HTMLElement | null;
    private desktopMenu: HTMLElement | null;
    private mobileStepsButtons: HTMLElement[] = [];
    private mobileStepsListener: EventListenerOrEventListenerObject;
    private menuState: boolean = false;
    private tabletClose: HTMLElement | null;
    private desktopSlider: HTMLElement | null;
    private desktopSliderImages: HTMLElement[] = [];

    constructor(options: IMenuOptions) {
        this.wrapper = options.wrapper;
        this.headerWrap = options.headerWrap;
        this.openButton = options.openButton;
        this.overlay = this.wrapper.querySelector<HTMLElement>('.j-menu-overlay');
        this.tabletClose = this.wrapper.querySelector<HTMLElement>('.j-menu-mobile-tablet-close');
        this.isMobile = Utils.getWindowWidth() < EBreakpoint.DESKTOP;
        this.isFullHD = Utils.getWindowWidth() >= EBreakpoint.FULLHD;
    }

    init(): void {
        this.#bindEvents();
        this.#setElements();
        this.#subscribes();

        if (this.isFullHD) {
            this.#initDesktopSlider();
        }
    }

    /**
     * Привязка событий
     */
    #bindEvents(): void {
        RESIZE_EVENTS.forEach((event: string) => {
            window.addEventListener(event, Utils.debounce(() => {
                this.isMobile = Utils.getWindowWidth() < EBreakpoint.DESKTOP;
                this.isFullHD = Utils.getWindowWidth() >= EBreakpoint.FULLHD;

                if (this.isFullHD) {
                    this.#initDesktopSlider();
                }

                if (this.isMobile) {
                    if (this.menuState) {
                        this.#toggleMobileContent();
                        this.menuState = false;
                    }

                    this.wrapper.classList.add(CLASS_HIDE);
                } else if (!this.isMobile && this.menuState) {
                    this.#toggleDesktopContent();
                    this.menuState = false;
                } else {
                    this.mobileMenuDynamic && Utils.clearHtml(this.mobileMenuDynamic);
                    this.wrapper.classList.remove(CLASS_HIDE);
                }
            }, 100));
        });

        this.openButton.addEventListener('click', () => {
            if (!this.isMobile && !this.menuState) {
                return;
            }

            this.#toggleMenu();
        });

        this.openButton.addEventListener('mouseenter', () => {
            if (this.menuState || this.isMobile) {
                return;
            }

            this.#toggleMenu();
        });

        this.overlay?.addEventListener('click', Utils.throttle(() => {
            this.#toggleMenu();
        }, 310));

        this.overlay?.addEventListener('mouseenter', Utils.throttle(() => {
            if (this.isMobile) {
                return;
            }

            this.#toggleMenu();
        }, 310));

        this.tabletClose?.addEventListener('click', () => {
            this.#toggleMenu();
        });

        this.#bindListeners();
    }

    /**
     * Инициализация слайдера на десктопе
     * @private
     */
    #initDesktopSlider(): void {
        if (!this.desktopSlider || this.desktopSlider.dataset['init']) {
            return;
        }

        const sliderOptions = {
            loop    : true,
            autoplay: {
                delay: 4700
            },
            effect       : 'fade',
            slidesPerView: 1,
            spaceBetween : 0,
            speed        : 300,
            navigation   : {
                nextEl: '.j-menu-desktop-arrow-next',
                prevEl: '.j-menu-desktop-arrow-prev'
            },
            // кастомный флаг для установки дата атрибута после инита слайдера
            initFlag: true
        };

        initSlider(this.desktopSlider, {sliderOptions});
    }

    /**
     * Собираем элементы меню для работы
     * @private
     */
    #setElements(): void {
        this.mobileMenu = this.wrapper.querySelector<HTMLElement>('.j-menu-mobile');
        this.desktopMenu = this.wrapper.querySelector<HTMLElement>('.j-menu-desktop');

        if (this.mobileMenu) {
            this.mobileMenuDynamic = this.mobileMenu.querySelector<HTMLElement>('.j-menu-mobile-content');
            this.mobileStepsButtons = [...this.mobileMenu.querySelectorAll<HTMLElement>('.j-mobile-step-button')];
        }

        if (!this.isMobile) {
            this.wrapper.classList.remove(CLASS_HIDE);
            this.wrapper.classList.remove(CLASS_ACTIVE);
        }

        if (this.desktopMenu) {
            this.desktopSliderImages =
                [...this.desktopMenu.querySelectorAll<HTMLElement>('.j-menu-desktop-slider-image')];
            this.desktopSlider = this.desktopMenu.querySelector<HTMLElement>('.j-menu-desktop-slider');
        }
    }

    #subscribes(): void {
        observer.subscribe('estate-menu:open', () => {
            if (!this.menuState) {
                return;
            }

            this.#toggleMenu();
        });
    }

    /**
     * Вешаем слушателей на действия пользователей в меню
     * @private
     */
    #bindListeners(): void {
        const that = this;

        const listener = (event: Event): void => {
            const target: EventTarget | null = event.target;

            if (target && target instanceof HTMLElement) {
                this.#manipulateStep(target);
            }
        };

        this.mobileStepsListener = listener.bind(that);
    }

    #toggleMenu(): void {
        this.isMobile ? this.#toggleMobileContent() : this.#toggleDesktopContent();

        if (!this.isMobile && !this.menuState) {
            observer.publish('main-menu:open');
        }

        const method = this.menuState ? 'remove' : 'add';
        const timeoutTime = this.isMobile ? 50 : 300;

        this.openButton.classList[method](CLASS_ACTIVE);
        this.wrapper.classList[method](CLASS_ACTIVE);
        this.headerWrap.classList[method](CLASS_OPEN);

        if (this.menuState) {
            setTimeout(() => {
                this.wrapper.classList.add(CLASS_HIDE);
            }, timeoutTime);
        } else {
            this.wrapper.classList.remove(CLASS_HIDE);
        }

        this.menuState = !this.menuState;
    }

    /**
     * Инициализация работы мобильного меню
     * @private
     */
    #toggleMobileContent(): void {
        if (!this.mobileMenu) {
            return;
        }

        const content = this.mobileMenu.dataset['content'] || '';
        const parsedContent = JSON.parse(atob(content));
        const listenerMethod = this.menuState ? 'removeEventListener' : 'addEventListener';
        const mainMobile = this.mobileMenu.querySelector<HTMLElement>('.j-menu-mobile-main');

        this.#createMobileSteps(parsedContent);
        this.#setElements();

        if (mainMobile) {
            this.menuState ?
                Utils.bodyStatic() :
                Utils.bodyFixed(mainMobile);
        }

        this.mobileStepsButtons.forEach((button: HTMLElement) => {
            button[listenerMethod]('click', this.mobileStepsListener);
        });
    }

    /**
     * Инициализация работы десктопного меню со списками
     * @param {string} type - тип события - клик или mouseenter
     * @private
     */
    #toggleDesktopContent(): void {
        if (!this.desktopMenu) {
            return;
        }

        if (!this.desktopMenu.dataset['init']) {
            this.desktopSliderImages.forEach((image: HTMLElement) => {
                this.#createDesktopImage(image);
            });

            this.desktopMenu.dataset['init'] = 'true';
        }

        this.menuState ?
            Utils.bodyStatic() :
            Utils.bodyFixed(this.desktopMenu);
    }

    /**
     * Создаем экраны мобильного меню при его открытии - небольшая оптимизация
     * для pageSpeed - меньше дом элементов и меньше предзагружаемых изображений
     * @param {IMenuContent} steps - объект с элементами меню
     */
    #createMobileSteps(steps: IMenuContent): void {
        if (!steps?.mobile || !this.mobileMenuDynamic) {
            return;
        }

        Utils.clearHtml(this.mobileMenuDynamic);

        for (const key in steps.mobile) {
            if (Object.prototype.hasOwnProperty.call(steps.mobile, key)) {
                const element = itemTemplate(steps.mobile[key]);

                Utils.insetContent(this.mobileMenuDynamic, element);
            }
        }
    }

    /**
     * Переход по пунктам мобильного меню
     * @param {HTMLElement} target - элемент куда переключиться
     */
    #manipulateStep(target: HTMLElement): void {
        const view = target.getAttribute('data-id');
        const direction = target.getAttribute('data-direction');

        if (direction === 'next' && view) {
            const viewElement = this.mobileMenu?.querySelector(`[data-view="${view}"]`);

            if (viewElement) {
                viewElement.classList.add(CLASS_OPEN);

                const scrollElement = viewElement.querySelector<HTMLElement>('.j-menu-scroll-list');

                if (scrollElement) {
                    Utils.bodyFixed(scrollElement);
                }
            }
        } else if (direction === 'prev') {
            const viewElement = target.closest('.j-menu-mobile-view');

            viewElement?.classList.remove(CLASS_OPEN);
        }
    }

    /**
     * Создаем изображения в десктопном меню (как бы лейзи лоадер)
     * @param {HTMLElement} image - элемент содержащий данные о будущем
     * изображении и куда оно будет помещенно
     * @private
     */
    #createDesktopImage(image: HTMLElement): void {
        if (!image) {
            return;
        }

        const img = new Image();

        img.src = image.dataset['src'] || '';
        img.alt = image.dataset['alt'] || '';

        image.appendChild(img);
    }
}

export default Menu;
